'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CommandLoading } from 'cmdk';
import {
  Building2Icon,
  CheckCheckIcon,
  FlagIcon,
  RotateCcwIcon,
  RouteIcon,
  User2Icon,
  VenetianMaskIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuthCtx } from '@/components/providers/auth-provider';
import { FEATURE_FLAGS, useFeatureFlagCtx } from '@/components/providers/feature-flag-provider';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useRouter } from '@/lib/router';
import { cn } from '@/lib/utils';

type CmdTab = 'home' | 'office-switcher' | 'feature-flags';

interface ContextType {
  tab: CmdTab;
  setTab: (v: CmdTab) => void;
  setShouldFilter: (v: boolean) => void;
  close: () => void;
}

const Context = createContext<ContextType | null>(null);

function useCmdCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useCmdCtx must be used within a Provider');
  }
  return ctx;
}

export function CommandMenu() {
  const { isSuperuser, isActuallySuperuser } = useAuthCtx();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [shouldFilter, setShouldFilter] = useState(true);
  const [tab, setTab] = useState<CmdTab>('home');
  const isHome = tab === 'home';

  useEffect(() => {
    if (!isSuperuser && !isActuallySuperuser) {
      return;
    }

    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isSuperuser, isActuallySuperuser]);

  useEffect(() => {
    if (!open) {
      setTab('home');
      setInputValue('');
    }
  }, [open]);

  const onChangeTab = useCallback((tab: CmdTab) => {
    setTab(tab);
    setInputValue('');
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Context value={{ tab, setTab: onChangeTab, setShouldFilter, close: onClose }}>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={shouldFilter}>
        <CommandInput
          placeholder='Type a command or search...'
          value={inputValue}
          onValueChange={(value) => {
            setInputValue(value);
          }}
          onKeyDown={(e) => {
            if (isHome || inputValue.length) {
              return;
            }
            if (e.key === 'Backspace') {
              setTab('home');
            }
          }}
        />
        <CommandList>
          {tab === 'home' && <CommandsHome />}
          {tab === 'office-switcher' && <CommandsOfficeSwitcher />}
          {tab === 'feature-flags' && <CommandFeatureFlags />}
        </CommandList>
      </CommandDialog>
    </Context>
  );
}

const NAV_ITEMS = [
  {
    label: 'Customers',
    icon: User2Icon,
    href: '/customers',
  },
  {
    label: 'Routes',
    icon: RouteIcon,
    href: '/routes',
  },
];

function CommandsHome() {
  const router = useRouter();
  const { setTab } = useCmdCtx();
  const { isSuperuser, isActuallySuperuser, toggleSuperuserView } = useAuthCtx();
  return (
    <CommandGroup heading='Suggestions'>
      {isSuperuser && (
        <CommandItem
          onSelect={() => {
            setTab('office-switcher');
          }}
        >
          <Building2Icon />
          <span>Switch Office</span>
        </CommandItem>
      )}
      {isSuperuser && (
        <CommandItem
          onSelect={() => {
            setTab('feature-flags');
          }}
        >
          <FlagIcon />
          <span>Feature Flags</span>
        </CommandItem>
      )}
      {isActuallySuperuser && (
        <CommandItem
          onSelect={() => {
            toggleSuperuserView();
          }}
        >
          <VenetianMaskIcon />
          <span>
            Switch to <span className='font-medium'>{isSuperuser ? 'regular' : 'superuser'}</span> view
          </span>
        </CommandItem>
      )}
      {NAV_ITEMS.map((item) => (
        <CommandItem
          key={item.href}
          onSelect={() => {
            close();
            router.push(item.href);
          }}
        >
          <item.icon />
          <span>
            Go to <span className='font-medium'>{item.label}</span>
          </span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function CommandsOfficeSwitcher() {
  const { api } = useApiV1Ctx();
  const { close } = useCmdCtx();
  const { data: offices = [], isLoading } = api.offices.listAllOffices.useQuery();

  const mutation = api.employees.switchToOffice.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Switched to office employee');
      window.location.reload();
    },
  });

  return (
    <CommandGroup heading='Switch Office'>
      {isLoading && (
        <CommandLoading className='flex h-24 animate-pulse items-center justify-center rounded-lg bg-muted text-center text-sm font-medium text-muted-foreground'>
          Loading offices...
        </CommandLoading>
      )}
      {offices.map((office) => (
        <CommandItem
          key={office.id}
          onSelect={() => {
            mutation.mutate({
              query: {
                office_id: office.id,
              },
            });
            close();
          }}
        >
          <Building2Icon />
          <span>{office.name}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function CommandFeatureFlags() {
  const { isFeatureEnabled, setFeatureFlag, enableAll, reset } = useFeatureFlagCtx();
  const features = useMemo(() => {
    return FEATURE_FLAGS.map((name) => ({ name: name, enabled: isFeatureEnabled(name) }));
  }, [isFeatureEnabled]);
  const enabledCount = useMemo(() => features.filter((v) => v.enabled).length, [features]);

  return (
    <>
      <CommandGroup heading='Quick actions'>
        <CommandItem key='enable-all-features' onSelect={() => enableAll()}>
          <CheckCheckIcon />
          Enable all features{' '}
          <span className='text-muted-foreground'>
            ({enabledCount} / {FEATURE_FLAGS.length})
          </span>
        </CommandItem>
        <CommandItem key='reset-features' onSelect={() => reset()}>
          <RotateCcwIcon />
          Reset feature overwrites
        </CommandItem>
      </CommandGroup>
      <CommandGroup heading='All feature flags'>
        {features.map((v) => (
          <CommandItem key={v.name} onSelect={() => setFeatureFlag(v.name, !v.enabled)}>
            <div className={cn('mr-1 size-2 rounded-full border', v.enabled && 'border-primary bg-primary')} />
            {v.name}
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}
